import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { DollarSign, TrendingUp, TrendingDown, Trash2 } from 'lucide-react';

const usd = (n) => `$${(Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
};

/**
 * Recent entries table for the ledger tab. Pure UI — parent owns filter/delete handlers.
 */
const LedgerEntriesTable = ({ visibleEntries, entries, estimates, filterType, setFilterType, onDelete }) => (
  <Card className="bg-slate-900 border-slate-800">
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <CardTitle className="text-base text-white flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-green-400" /> Recent entries
        </CardTitle>
        <div className="inline-flex rounded border border-slate-700 overflow-hidden">
          {['all', 'income', 'expense'].map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`ledger-filter-${t}`}
              onClick={() => setFilterType(t)}
              className={`text-xs px-3 py-1 capitalize ${
                filterType === t ? 'bg-red-600 text-white' : 'bg-slate-900 text-gray-400 hover:bg-slate-800'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
    </CardHeader>
    <CardContent>
      {visibleEntries.length === 0 ? (
        <p className="text-gray-500 py-8 text-center" data-testid="ledger-empty">
          {entries.length === 0 ? 'No entries yet. Log your first income or expense above.' : 'No entries match this filter.'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/40 text-gray-400 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Category</th>
                <th className="px-3 py-2 font-medium text-right">Amount</th>
                <th className="px-3 py-2 font-medium">Note</th>
                <th className="px-3 py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleEntries.map((e) => {
                const est = e.estimate_id ? estimates.find((x) => x.id === e.estimate_id) : null;
                return (
                  <tr key={e.id} className="border-t border-slate-800 hover:bg-slate-900/70" data-testid={`ledger-row-${e.id}`}>
                    <td className="px-3 py-2 text-gray-300 whitespace-nowrap">{fmtDate(e.date)}</td>
                    <td className="px-3 py-2">
                      {e.type === 'income' ? (
                        <Badge variant="secondary" className="bg-green-600/20 text-green-300 border-green-600/30">
                          <TrendingUp className="w-3 h-3 mr-1" /> Income
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-red-600/20 text-red-300 border-red-600/30">
                          <TrendingDown className="w-3 h-3 mr-1" /> Expense
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-300">{e.category}</td>
                    <td className={`px-3 py-2 font-bold text-right tabular-nums ${e.type === 'income' ? 'text-green-300' : 'text-red-300'}`}>
                      {e.type === 'income' ? '+' : '−'}{usd(e.amount)}
                    </td>
                    <td className="px-3 py-2 text-gray-400 max-w-md">
                      {est && (
                        <Badge variant="secondary" className="bg-amber-600/15 text-amber-300 border-amber-600/30 mr-2 font-mono text-[10px]">
                          {est.estimate_number}
                        </Badge>
                      )}
                      <span className="line-clamp-2 inline">{e.note || '—'}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm" variant="outline"
                        data-testid={`ledger-delete-${e.id}`}
                        onClick={() => onDelete(e)}
                        title="Delete entry"
                        aria-label="Delete entry"
                        className="border-slate-700 text-gray-400 hover:bg-red-950/40 hover:text-red-400 hover:border-red-900/60"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);

export default LedgerEntriesTable;
